import { Component, HostListener, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, shareReplay, switchMap, take } from 'rxjs/operators';

import {
  getAssociationPhaseStatesByTypesFactory,
  getMatchedCandidatePhase,
} from '@modules/assigns/store';
import { MojoBatchService } from 'mojo-batch';
import { noteBatchRejectedModalShow } from '@shared/modal';
import { AssociationStatusConfigurationTypes } from '@shared/models';
import {
  AssociationsSelectorObj,
  checkAssociationByCandidateIdAndJobId,
} from '@collections/associations';

interface CandidateItemWithAssociationStatus {
  candidateId: string;
  phaseId: string;
}

interface CandidateItemWithAssociationStatusAndRejection
  extends CandidateItemWithAssociationStatus {
  rejectStateId: string;
}

@Component({
  selector: 'mojo-batch-operation-reject-candidate',
  templateUrl: './batch-operation-reject-candidate.component.html',
  styleUrls: ['./batch-operation-reject-candidate.component.scss'],
})
export class BatchOperationRejectCandidateComponent implements OnChanges {
  @Input() jobId: number;
  @Input() isNotDisabled = true;

  disabled$ = this.selectionManagerService.selection$.pipe(
    map((selection) => selection.length === 0),
    shareReplay(1),
  );

  private candidatesStatus$: Observable<CandidateItemWithAssociationStatus[]>;
  private candidatesWithRejection$: Observable<CandidateItemWithAssociationStatusAndRejection[]>;

  constructor(private selectionManagerService: MojoBatchService, private store: Store) {}

  private initCandidates() {
    this.candidatesStatus$ = combineLatest([
      this.selectionManagerService.selection$,
      this.store.select(AssociationsSelectorObj.getJobAssociations(this.jobId)),
      this.store.select(getMatchedCandidatePhase, { jobId: this.jobId }),
    ]).pipe(
      map(([cadidateIds, associations, { phase }]) =>
        cadidateIds.length > 0
          ? cadidateIds.map((candidateId) => {
              const candidateAssociation = associations.find(
                checkAssociationByCandidateIdAndJobId(candidateId, this.jobId),
              );
              return {
                candidateId,
                phaseId: !!candidateAssociation?.status ? candidateAssociation.status : phase.id, // default to matched candidates phase
              };
            })
          : [],
      ),
      shareReplay(1),
    );
  }

  private initCandidatesWithRejectionState() {
    this.candidatesWithRejection$ = this.candidatesStatus$.pipe(
      switchMap((items: CandidateItemWithAssociationStatus[]) =>
        combineLatest<CandidateItemWithAssociationStatusAndRejection[]>(
          items.map(({ candidateId, phaseId }) =>
            this.store
              .select(getAssociationPhaseStatesByTypesFactory, {
                jobId: this.jobId,
                configurationStateId: phaseId,
                type: AssociationStatusConfigurationTypes.REJECTED,
              })
              .pipe(
                map((rejectStates) => ({
                  candidateId,
                  phaseId,
                  rejectStateId: rejectStates[0].id,
                })),
              ),
          ),
        ),
      ),
      shareReplay(1),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.jobId?.currentValue !== changes.jobId?.previousValue) {
      this.initCandidates();
      this.initCandidatesWithRejectionState();
    }
  }

  @HostListener('click')
  reject() {
    if (this.isNotDisabled) {
      this.disabled$
        .pipe(
          switchMap((disabled) => (disabled ? of([]) : this.candidatesWithRejection$)),
          take(1),
          filter((items) => items.length > 0),
        )
        .subscribe((items: CandidateItemWithAssociationStatusAndRejection[]) => {
          // TODO: this solution assumes items are under same phase and
          // are moved to one specific rejection state it probably would require openning multiple
          // modals or new kind of modal form moving candidtes from diffrent phases to diffrent states
          this.store.dispatch(
            noteBatchRejectedModalShow({
              payload: {
                jobId: this.jobId,
                candidateIds: !!items ? items.map(({ candidateId }) => candidateId) : [],
                phaseId: items[0].phaseId,
                rejectionStateId: items[0].rejectStateId,
              },
            }),
          );
        });
    }
  }
}
